import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { 
  ArrowLeft, UserCog, Plus, Trash2, Mail, Shield, Heart, FileText, 
  Home, Send, Loader2, Check, X, Clock, Settings, AlertTriangle, Phone
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { haptics } from '@/utils/haptics';
import { useDemo } from '@/contexts/DemoContext';
import { ProtectedFeaturePage } from '@/components/ProtectedFeaturePage';

/**
 * CaregiverPage - Manage trusted caregivers who can help update your binder
 * FAMILY FEATURE - Requires Family subscription
 * - Invite caregivers
 * - Set permissions and approval requirements
 * - View pending invitations
 */

const CAREGIVER_PERMISSIONS = [
  { id: 'medical', label: 'Medical Info', icon: Heart, description: 'View and update medical information' },
  { id: 'contacts', label: 'Emergency Contacts', icon: Phone, description: 'Manage emergency contacts' },
  { id: 'documents', label: 'Documents', icon: FileText, description: 'Upload and manage documents' },
  { id: 'household', label: 'Household Info', icon: Home, description: 'Update household details' },
];

const CaregiverContent = () => {
  const navigate = useNavigate();
  const { isDemoMode, getCaregiverAccess, updateCaregiverAccess } = useDemo();
  const [loading, setLoading] = useState(true);
  const [caregivers, setCaregivers] = useState([]);
  
  // Invite dialog
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [newCaregiver, setNewCaregiver] = useState({
    name: '',
    email: '',
    relationship: 'caregiver',
    permissions: ['medical', 'contacts'],
    requires_approval: true
  });
  
  // Permissions dialog
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [selectedCaregiver, setSelectedCaregiver] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState([]);
  const [editingRequiresApproval, setEditingRequiresApproval] = useState(true);

  useEffect(() => {
    fetchCaregivers();
  }, [isDemoMode]);

  const fetchCaregivers = async () => {
    try {
      setLoading(true);
      
      // Demo mode: use local demo data
      if (isDemoMode) {
        const caregiverAccess = getCaregiverAccess() || {};
        setCaregivers(caregiverAccess.caregivers || []);
        setLoading(false);
        return;
      }
      
      const response = await API.get('/emergency-binder/caregivers');
      setCaregivers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch caregivers:', error);
      if (!isDemoMode) {
        toast.error('Failed to load caregivers');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!newCaregiver.name || !newCaregiver.email) {
      toast.error('Please provide name and email');
      return;
    }

    try {
      setInviting(true);
      haptics.tap();
      
      // Demo mode: add to local state
      if (isDemoMode) {
        const newCaregiverItem = {
          id: `caregiver_${Date.now()}`,
          ...newCaregiver,
          status: 'pending',
          invited_at: new Date().toISOString()
        };
        const updatedCaregivers = [...caregivers, newCaregiverItem];
        setCaregivers(updatedCaregivers);
        updateCaregiverAccess({ caregivers: updatedCaregivers });
        toast.success(`Invitation sent to ${newCaregiver.name} (demo)`);
        setShowInviteDialog(false);
        setNewCaregiver({
          name: '',
          email: '',
          relationship: 'caregiver',
          permissions: ['medical', 'contacts'],
          requires_approval: true
        });
        setInviting(false);
        return;
      }
      
      await API.post('/emergency-binder/caregivers/invite', newCaregiver);
      toast.success(`Invitation sent to ${newCaregiver.name}`);
      setShowInviteDialog(false);
      setNewCaregiver({
        name: '',
        email: '',
        relationship: 'caregiver',
        permissions: ['medical', 'contacts'],
        requires_approval: true
      });
      fetchCaregivers();
    } catch (error) {
      console.error('Failed to send invite:', error);
      if (!isDemoMode) {
        toast.error(error.response?.data?.detail || 'Failed to send invitation');
      }
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveCaregiver = async (caregiverId) => {
    try {
      haptics.tap();
      
      // Demo mode: remove from local state
      if (isDemoMode) {
        const updatedCaregivers = caregivers.filter(c => c.id !== caregiverId);
        setCaregivers(updatedCaregivers);
        updateCaregiverAccess({ caregivers: updatedCaregivers });
        toast.success('Caregiver removed (demo)');
        return;
      }
      
      await API.delete(`/emergency-binder/caregivers/${caregiverId}`);
      toast.success('Caregiver removed');
      fetchCaregivers();
    } catch (error) {
      console.error('Failed to remove caregiver:', error);
      if (!isDemoMode) {
        toast.error('Failed to remove caregiver');
      }
    }
  };

  const handleUpdatePermissions = async () => {
    if (!selectedCaregiver) return;
    
    try {
      haptics.tap();
      
      // Demo mode: update local state
      if (isDemoMode) {
        const updatedCaregivers = caregivers.map(c => 
          c.id === selectedCaregiver.id 
            ? { ...c, permissions: editingPermissions, requires_approval: editingRequiresApproval }
            : c
        );
        setCaregivers(updatedCaregivers);
        updateCaregiverAccess({ caregivers: updatedCaregivers });
        toast.success('Caregiver settings updated (demo)');
        setShowPermissionsDialog(false);
        return;
      }
      
      await API.put(`/emergency-binder/caregivers/${selectedCaregiver.id}/permissions`, {
        permissions: editingPermissions,
        requires_approval: editingRequiresApproval
      });
      toast.success('Caregiver settings updated');
      setShowPermissionsDialog(false);
      fetchCaregivers();
    } catch (error) {
      console.error('Failed to update permissions:', error);
      if (!isDemoMode) {
        toast.error('Failed to update settings');
      }
    }
  };

  const openPermissionsDialog = (caregiver) => {
    setSelectedCaregiver(caregiver);
    setEditingPermissions(caregiver.permissions || []);
    setEditingRequiresApproval(caregiver.requires_approval !== false);
    setShowPermissionsDialog(true);
  };

  const togglePermission = (permissionId) => {
    setEditingPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(p => p !== permissionId)
        : [...prev, permissionId]
    );
  };

  const toggleNewPermission = (permissionId) => {
    setNewCaregiver(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const getPermissionLabel = (id) => {
    const perm = CAREGIVER_PERMISSIONS.find(p => p.id === id);
    return perm?.label || id;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'accepted':
        return <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="caregiver-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/emergency-binder')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">Caregiver Access</h1>
              <p className="text-xs text-slate-500">Trusted helpers for your binder</p>
            </div>
            <Button 
              onClick={() => setShowInviteDialog(true)}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="invite-caregiver-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Info Banner */}
        <Card className="border-teal-200 bg-teal-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <UserCog className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-teal-800 mb-1">About Caregiver Mode</h3>
                <p className="text-sm text-teal-700">
                  Caregivers are trusted people who can help manage and update your emergency 
                  information. You control what they can access and whether their changes 
                  need your approval.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Caregivers List */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <UserCog className="h-5 w-5 text-teal-500" />
              Your Caregivers ({caregivers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {caregivers.length === 0 ? (
              <div className="text-center py-8">
                <UserCog className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No caregivers added yet</p>
                <p className="text-sm text-slate-400 mt-1">
                  Add a trusted person to help manage your emergency information
                </p>
                <Button 
                  onClick={() => setShowInviteDialog(true)} 
                  className="mt-4" 
                  variant="outline"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Caregiver
                </Button>
              </div>
            ) : (
              caregivers.map((caregiver) => (
                <div key={caregiver.id} className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                        <UserCog className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-slate-800">{caregiver.name}</h4>
                          {getStatusBadge(caregiver.status)}
                        </div>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {caregiver.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {caregiver.status === 'accepted' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openPermissionsDialog(caregiver)}
                          className="text-slate-500"
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500"
                        onClick={() => handleRemoveCaregiver(caregiver.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Permissions */}
                  <div className="flex flex-wrap gap-1 mb-2">
                    {caregiver.permissions?.map(perm => (
                      <Badge key={perm} variant="secondary" className="text-xs">
                        {getPermissionLabel(perm)}
                      </Badge>
                    ))}
                  </div>
                  
                  {/* Approval status */}
                  {caregiver.status === 'accepted' && (
                    <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">
                      {caregiver.requires_approval ? (
                        <>
                          <AlertTriangle className="h-3 w-3 text-amber-500" />
                          Changes require your approval
                        </>
                      ) : (
                        <>
                          <Check className="h-3 w-3 text-emerald-500" />
                          Can make changes directly
                        </>
                      )}
                    </div>
                  )}
                  
                  {caregiver.status === 'pending' && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Waiting for {caregiver.name} to accept invitation
                    </p>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Security Note */}
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-amber-800 mb-1">Security Note</h3>
                <p className="text-sm text-amber-700">
                  Only add caregivers you completely trust. They will have access to sensitive 
                  medical and personal information. You can revoke access at any time.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Caregiver</DialogTitle>
            <DialogDescription>
              Invite a trusted person to help manage your emergency information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={newCaregiver.name}
                  onChange={(e) => setNewCaregiver({...newCaregiver, name: e.target.value})}
                  placeholder="Full name"
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Input
                  value={newCaregiver.relationship}
                  onChange={(e) => setNewCaregiver({...newCaregiver, relationship: e.target.value})}
                  placeholder="e.g., Nurse, Helper"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newCaregiver.email}
                onChange={(e) => setNewCaregiver({...newCaregiver, email: e.target.value})}
                placeholder="email@example.com"
              />
            </div>
            
            <div className="space-y-3">
              <Label>Permissions</Label>
              <div className="space-y-2">
                {CAREGIVER_PERMISSIONS.map((perm) => {
                  const Icon = perm.icon;
                  return (
                    <div 
                      key={perm.id}
                      className="flex items-center space-x-3 p-2 rounded hover:bg-slate-50"
                    >
                      <Checkbox
                        id={`new-cg-${perm.id}`}
                        checked={newCaregiver.permissions.includes(perm.id)}
                        onCheckedChange={() => toggleNewPermission(perm.id)}
                      />
                      <div className="flex-1">
                        <label 
                          htmlFor={`new-cg-${perm.id}`} 
                          className="text-sm font-medium flex items-center gap-2 cursor-pointer"
                        >
                          <Icon className="h-4 w-4 text-slate-500" />
                          {perm.label}
                        </label>
                        <p className="text-xs text-slate-500">{perm.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-slate-800">Require approval for changes</p>
                <p className="text-xs text-slate-500">You'll review changes before they're saved</p>
              </div>
              <Switch
                checked={newCaregiver.requires_approval}
                onCheckedChange={(checked) => setNewCaregiver({...newCaregiver, requires_approval: checked})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleInvite}
              disabled={inviting}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {inviting ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" />Send Invite</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit {selectedCaregiver?.name}'s Access</DialogTitle>
            <DialogDescription>
              Update permissions and approval settings
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              {CAREGIVER_PERMISSIONS.map((perm) => {
                const Icon = perm.icon;
                return (
                  <div 
                    key={perm.id}
                    className="flex items-center space-x-3 p-2 rounded hover:bg-slate-50"
                  >
                    <Checkbox
                      id={`edit-cg-${perm.id}`}
                      checked={editingPermissions.includes(perm.id)}
                      onCheckedChange={() => togglePermission(perm.id)}
                    />
                    <div className="flex-1">
                      <label 
                        htmlFor={`edit-cg-${perm.id}`} 
                        className="text-sm font-medium flex items-center gap-2 cursor-pointer"
                      >
                        <Icon className="h-4 w-4 text-slate-500" />
                        {perm.label}
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-slate-800">Require approval</p>
                <p className="text-xs text-slate-500">Review changes before saving</p>
              </div>
              <Switch
                checked={editingRequiresApproval}
                onCheckedChange={setEditingRequiresApproval}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPermissionsDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdatePermissions}
              className="bg-teal-600 hover:bg-teal-700"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Wrap content with feature protection - Family tier required
const CaregiverPage = () => (
  <ProtectedFeaturePage
    feature="caregiver_access"
    title="Caregiver Access"
    description="Invite trusted caregivers to help manage your emergency binder with customizable permissions."
    backPath="/emergency-binder"
  >
    <CaregiverContent />
  </ProtectedFeaturePage>
);

export default CaregiverPage;
